// src/components/home/StepsSection.jsx
import * as React from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  stepConnectorClasses,
  Paper,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";

import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const steps = [
  {
    icon: CalendarTodayIcon,
    title: "Agendá tu cita",
    description: "Elegí la fecha y hora que más te convenga.",
    color: "primary",
  },
  {
    icon: WhatsAppIcon,
    title: "Hablá con nuestro equipo",
    description: "Te asesoramos y resolvemos todas tus dudas.",
    color: "success",
  },
  {
    icon: AssignmentIcon,
    title: "Envíanos tu información",
    description: "Completá los datos necesarios para iniciar.",
    color: "info",
  },
  {
    icon: CheckCircleIcon,
    title: "Recibí tu plan personalizado",
    description: "Solución adaptada a tus necesidades.",
    color: "warning",
  },
  {
    icon: TrendingUpIcon,
    title: "Comenzá a trabajar con nosotros",
    description: "Implementá tu plan y seguí los resultados.",
    color: "secondary",
  },
];

const ICON_SIZE = 56;           // mismo tamaño que el círculo del ícono
const CONNECTOR_GAP = 6;        // separación extra entre línea e ícono

const ColoredConnector = styled(StepConnector)(({ theme }) => ({
  // Colocamos la línea un poco por DEBAJO del centro del ícono
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: ICON_SIZE / 2, // 56/2 + 6 = 34px
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,                              // un poco más fina
    border: 0,
    borderRadius: 3,
    backgroundColor: theme.palette.action.disabledBackground,
  },
  zIndex: 0, // por debajo de los íconos
}));

/* — Icono de paso personalizado — */
function CustomStepIcon(props) {
  const { className, icon } = props;
  const theme = useTheme();
  const idx = Number(icon) - 1;
  const StepIcon = steps[idx].icon;
  const paletteKey = steps[idx].color;

  return (
    <Box
      className={className}
      sx={{
        width: ICON_SIZE,
        height: ICON_SIZE,
        borderRadius: "50%",
        bgcolor: alpha(theme.palette[paletteKey].main, 0.95),
        color: "#fff",
        boxShadow: "0 8px 20px rgba(0,0,0,.15)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all .2s ease",
        position: "relative",
        zIndex: 1, // por encima del conector
        // HALO: mismo fondo que la sección para “cortar” el conector
        outline: `6px solid ${theme.palette.grey[50]}`, // o theme.palette.background.default si preferís
      }}
    >
      <StepIcon sx={{ fontSize: 28 }} />
    </Box>
  );
}


/* — Contenido del label (título + descripción) — */
function StepLabelContent({ title, description }) {
  return (
    <Box sx={{ textAlign: "center" }}>
      <Typography
        sx={{
          fontWeight: 800,
          letterSpacing: "-.02em",
          fontSize: { xs: "clamp(1.05rem, 3.5vw, 1.25rem)", md: "1.2rem" },
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>
      <Typography
        color="text.secondary"
        sx={{
          mt: 0.5,
          fontSize: { xs: ".95rem", md: "1rem" },
          lineHeight: 1.55,
        }}
      >
        {description}
      </Typography>
    </Box>
  );
}

export default function StepsSection() {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 10 },
        px: 2,
        background: `linear-gradient(135deg, ${theme.palette.grey[50]}, ${theme.palette.grey[100]})`,
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        {/* Título */}
        <Typography
          align="center"
          sx={{
            fontWeight: 800,
            mb: { xs: 6, md: 8 },
            letterSpacing: "-.5px",
            fontSize: {
              xs: "clamp(1.8rem, 6vw, 2.2rem)",
              md: "clamp(2.2rem, 2.5vw, 2.8rem)",
            },
          }}
        >
          Tu camino para{" "}
          <Box component="span" sx={{ color: theme.palette.primary.main }}>
            trabajar con nosotros
          </Box>
        </Typography>

        {/* Stepper */}
        {isMdUp ? (
          /* — Horizontal (desktop) — */
<Stepper
  alternativeLabel
  activeStep={steps.length}
  connector={<ColoredConnector />}
  sx={{
    px: { md: 1, lg: 2 },
    "& .MuiStep-root": { maxWidth: "none", flex: 1 },
    "& .MuiStepLabel-labelContainer": { mt: 1 }, // ← aire extra
  }}
>

            {steps.map((s, i) => (
              <Step key={i}>
                <StepLabel StepIconComponent={CustomStepIcon}>
                  <StepLabelContent
                    title={s.title}
                    description={s.description}
                  />
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        ) : (
          /* — Vertical (mobile) — */
          <Box sx={{ display: "grid", gap: 2 }}>
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <Paper
                  key={i}
                  elevation={1}
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    display: "grid",
                    gridTemplateColumns: "56px 1fr",
                    gap: 2,
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      bgcolor: theme.palette[s.color].main, // fondo del color del paso
                      color: "#fff", // ícono blanco
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 8px 20px rgba(0,0,0,.12)",
                    }}
                  >
                    <Icon sx={{ fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        letterSpacing: "-.02em",
                        fontSize: "clamp(1.05rem, 3.8vw, 1.25rem)",
                        lineHeight: 1.2,
                      }}
                    >
                      {s.title}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      sx={{ mt: 0.5, fontSize: ".95rem", lineHeight: 1.55 }}
                    >
                      {s.description}
                    </Typography>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}

        {/* CTA centrado */}
        <Box
          sx={{
            mt: { xs: 7, md: 9 },
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            size="large"
            sx={{
              width: { xs: "100%", sm: "auto" },
              px: { xs: 4, md: 6 },
              py: 2,
              fontWeight: 700,
              borderRadius: 3,
              textTransform: "none",
              fontSize: { xs: "1rem", md: "1.1rem" },
              background: "linear-gradient(90deg, #FF5733, #FF8A5C)",
              boxShadow: "0 8px 24px rgba(255, 87, 51, 0.35)",
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              "&:hover": {
                background: "linear-gradient(90deg, #E64A19, #FF7043)",
                transform: "translateY(-1px)",
                boxShadow: "0 14px 32px rgba(255, 87, 51, 0.45)",
              },
            }}
          >
            Empezá Ahora <ArrowForwardIcon />
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
